// src/interfaces/catylexSearchTypes.ts

/**
 * Catylex Search Tool Type Definitions
 *
 * These types match the backend's transform_response() output in client.py.
 * The backend strips raw Catylex API data down to essential fields.
 *
 * The tool supports two actions: "search" (find contracts/clauses) and
 * "read" (load a found contract's text into the conversation as citable sentences).
 *
 * Backend response structure (via handler.py → frontend_chunk_data):
 * {
 *   type: "tool_result",
 *   tool_name: "catylex_search",
 *   tool_use_id: string,
 *   query: string,          // search + error paths only; absent on a successful read
 *   content: { status, search_type, original_query, ... }
 * }
 */

/**
 * Failure copy, in one place.
 *
 * Two surfaces, two jobs, and they must never be the same string: the SUMMARY is
 * the collapsed header ("what happened"), the DETAIL is the expanded body ("what
 * to do about it"). Defaulting both to the same sentence is what made expanding a
 * failed read pointless — the user paid a click to read the header again.
 *
 * These are the fallbacks used only when the backend sends no `message`. A real
 * backend message is always more specific and wins. Nothing developer-facing
 * belongs here: a lawyer reads these verbatim.
 */
export const CATYLEX_READ_ERROR_SUMMARY = "Contract could not be loaded";
export const CATYLEX_READ_ERROR_DETAIL =
  "This contract couldn't be loaded from Catylex. Ask to search within it instead, " +
  "or pick another contract from the search results.";
export const CATYLEX_SEARCH_ERROR_SUMMARY = "Search failed";
export const CATYLEX_SEARCH_ERROR_DETAIL =
  "The search couldn't be completed. Try rephrasing what you're looking for, or " +
  "name a specific contract or clause.";

/**
 * Copy for a search the backend deliberately did not run.
 *
 * Deliberately NOT in the block above: nothing failed here, and the words must
 * not imply it did. Catylex is capped at one search per response — the second
 * search call is answered with `status: "skipped"` instead of being sent — so
 * the user is looking at a note, not a problem.
 *
 * SUMMARY is only the fallback header: the backend's own `message` states the
 * fact and wins. DETAIL is always ours — the backend says what happened, the
 * add-in says what the user can do next.
 */
export const CATYLEX_SKIPPED_SUMMARY = "Search not run";
export const CATYLEX_SKIPPED_DETAIL =
  "Catylex allows one search per response. To search for something different, tell " +
  "Contract Space what you're after — a party, a contract type, a date range — and it " +
  "will search Catylex again in its next reply.";

// --- Individual result items ---

/**
 * A single metadata concept extracted from a contract (e.g., "Agreement Function").
 * Returned by the /api/v2/contract/metadata endpoint, transformed by the backend.
 * The backend restricts this to an allowlist of concept_names; values are the
 * concept's flat string values.
 */
export interface CatylexContractMetadataConcept {
  concept_name: string;
  values: string[];
}

/**
 * A party to a contract. `role` is passed through from the backend
 * (e.g. "away", "home", or any other role string).
 */
export interface CatylexContractParty {
  name: string;
  role: string;
}

/**
 * Contract object — matches transform_response output for search_type: "contracts".
 * `title` stays mapped to the filename for back-compat; `display_title` carries the
 * human-readable title and `file_name` the explicit filename. metadata is optionally
 * enriched for the top 3 contracts via the metadata API.
 */
export interface CatylexContract {
  id: string;
  /** Filename — kept for back-compat. Prefer `display_title`/`file_name` when present. */
  title: string;
  /** Explicit filename (e.g. "GPLNG-Zeus_Solutions_SCT_final.docx.pdf"). */
  file_name?: string;
  /** Human-readable title (e.g. "STATEMENT OF COMMERCIAL TERMS NO. 1"); "" if none. */
  display_title?: string;
  /** Agreement date as ISO 8601 (e.g. "2025-02-24T00:00:00Z"); "" if none. */
  agreement_date?: string;
  /** Every party with its role passed through from the backend. */
  parties?: CatylexContractParty[];
  /** Workspace folder path (e.g. "gp-15-04-25"); "" if none. */
  workspace_path?: string;
  status: string;
  workspace: string;
  created_at: string;
  updated_at: string;
  metadata?: CatylexContractMetadataConcept[];
  all_terms_report_url?: string;
}

/**
 * Sentence/clause result — matches transform_response output in client.py.
 * Carries the source contract's title/date/parties so the clause card can show
 * the same header as a contract result.
 */
export interface CatylexSentence {
  text: string;
  /** Source filename — kept for back-compat. */
  contract_name: string;
  contract_id: string;
  pages: number[];
  all_terms_report_url?: string;
  /** Source contract's human-readable title; "" if none. */
  display_title?: string;
  /** Source contract's agreement date as ISO 8601; "" if none. */
  agreement_date?: string;
  /** Source contract's parties with roles passed through. */
  parties?: CatylexContractParty[];
  /** Source contract's workspace folder path; "" if none. */
  workspace_path?: string;
}

// --- Search results container (matches transform_response output) ---

export interface CatylexSearchResults {
  total: number;
  /** Human-readable summary, e.g. "89 results (showing top 10)" */
  number_of_shown: string;
  contracts?: CatylexContract[];
  sentences?: CatylexSentence[];
}

// --- Suggestion object from "not_supported" responses ---

export interface CatylexSuggestionObject {
  suggestion_text: string;
  search_type: string;
  search_query: Record<string, unknown>;
}

export type CatylexSuggestion = string | CatylexSuggestionObject;

// --- Content payload types (inside the `content` wrapper) ---

export interface CatylexSearchSuccessContent {
  status: "success";
  /** Absent on the wire — the search payload predates `action` and is unchanged. */
  action?: "search";
  search_type: "contracts" | "sentences";
  original_query: string;
  interpreted_query: string;
  search_query?: Record<string, unknown>;
  search_results: CatylexSearchResults;
  pagination_mode?: boolean;
}

/**
 * Backend response for action: "read" — a contract's text pulled from Catylex's
 * all-terms report and loaded into the conversation as citable sentences.
 *
 * The document body is deliberately NOT on this type. It has no consumer in the
 * task pane (the card renders counts, never prose) and history substitutes a
 * placeholder for it, so shipping a whole contract's text here was pure waste.
 *
 * `truncated` is real: the all-terms report returns at most 1000 sentences per
 * request, so a contract longer than that window comes back as its leading
 * sentences with the flag set rather than being refused. The sentence counts are
 * the document's own, so the shortfall they state is exact.
 */
export interface CatylexReadContent {
  status: "success";
  action: "read";
  contract_id: string;
  file_name: string;
  display_title: string;
  /**
   * The only URL Catylex exposes for a contract, and it comes ONLY from a search
   * response — no read-path endpoint returns it. "" is therefore the normal case
   * for a cross-turn read; `useChatAnalysis` recovers it from session state.
   */
  all_terms_report_url: string;
  /** Catylex workspace the contract lives in; the backend rejects out-of-scope ones. */
  workspace: string;
  /**
   * Sentences in the whole document, vs. how many this read returned. The
   * all-terms report states the document's own total, so the pair is exact —
   * there are no character counters because there is nothing left to estimate.
   */
  total_sentences: number;
  sentences_returned: number;
  truncated: boolean;
}

export interface CatylexSearchNotSupportedContent {
  status: "not_supported";
  error_code: string;
  original_query: string;
  reason: string;
  suggestions: CatylexSuggestion[];
  available_concepts: string[];
}

export interface CatylexSearchErrorContent {
  status: "error";
  /** Which action failed. Absent on responses predating the read action. */
  action?: "search" | "read";
  original_query: string;
  message?: string;
  error_code?: string;
  /** Present only when `action` is "read". */
  contract_id?: string;
}

/**
 * A search the backend chose not to run, because one search had already run in
 * this response. Nothing was queried and nothing failed, so this is neither a
 * success nor an error — it is a note, and renders as one.
 */
export interface CatylexSearchSkippedContent {
  status: "skipped";
  /** Always "search": reads are not capped, so they are never skipped. */
  action: "search";
  /** The query that was not sent. */
  original_query: string;
  /** The backend's statement of what happened. Rendered verbatim. */
  message: string;
}

export type CatylexSearchContent =
  | CatylexSearchSuccessContent
  | CatylexReadContent
  | CatylexSearchNotSupportedContent
  | CatylexSearchErrorContent
  | CatylexSearchSkippedContent;

// --- Tool input type (captured from input_json_delta) ---

export interface CatylexToolInput {
  action: "search" | "read";
  query?: string;
  contractId?: string;
}

// --- Raw backend response (top-level chunk.text) ---

export interface CatylexSearchResult {
  type: "tool_result";
  tool_name: "catylex_search";
  tool_use_id: string;
  /** Search and error chunks carry the query; a successful read has none. */
  query?: string;
  // Content may have unknown status values — normalizer handles gracefully
  content: CatylexSearchContent | Record<string, unknown>;
}

// --- Normalized result for UI consumption ---

export interface NormalizedCatylexSearchResult {
  status: "success" | "not_supported" | "error" | "skipped";
  /**
   * Which tool action produced this result. Defaults to "search" so results
   * stored before the read action existed keep rendering as searches.
   */
  action?: "search" | "read";
  searchType?: "contracts" | "sentences";
  /** "" for a successful read — the model calls read by contract_id, not by query. */
  originalQuery: string;
  interpretedQuery?: string;
  // Success fields (search)
  totalResults?: number;
  /** Human-readable summary like "89 results (showing top 10)" */
  resultsSummary?: string;
  contracts?: CatylexContract[];
  sentences?: CatylexSentence[];
  paginationMode?: boolean;
  // Success fields (read)
  contractId?: string;
  fileName?: string;
  displayTitle?: string;
  /** "" when Catylex has no URL for the contract; absent on search results. */
  allTermsReportUrl?: string;
  workspace?: string;
  totalSentences?: number;
  sentencesReturned?: number;
  truncated?: boolean;
  // Not-supported fields
  reason?: string;
  suggestions?: CatylexSuggestion[];
  availableConcepts?: string[];
  // Error fields
  errorMessage?: string;
  errorCode?: string;
  // Skipped fields
  /** The backend's sentence for why nothing was searched. */
  skippedMessage?: string;
  // Metadata
  tool_use_id?: string;
}

// --- Type guard ---

/**
 * Type guard to check if an object is a CatylexSearchResult.
 * Detects by `tool_name === "catylex_search"` and checks for `content` wrapper.
 */
export function isCatylexSearchResult(obj: unknown): obj is CatylexSearchResult {
  if (!obj || typeof obj !== "object") return false;

  const candidate = obj as Record<string, unknown>;
  if (candidate.tool_name !== "catylex_search") return false;

  // The backend wraps results in a `content` field
  const content = candidate.content;
  if (!content || typeof content !== "object") return false;

  const inner = content as Record<string, unknown>;

  if (inner.status === "success") {
    // A read payload carries none of search's shape — no search_type, no
    // original_query, no search_results. Discriminate on `action` first or the
    // read chunk fails the search checks below and is silently dropped by the
    // router (it has no other branch to fall into).
    if (inner.action === "read") {
      // Check ONLY contract_id — the one field a read cannot lack, since the
      // backend echoes it from the request. Every additional `typeof` here is a
      // silent-drop hazard: this guard is the transport gate (chunkRouter's
      // `else if`), so the day the backend stops sending a field we assert on,
      // the whole card vanishes with no error anywhere. That is exactly what
      // happened when `content` was asserted here and then removed from the
      // payload. Let the normalizer tolerate missing optionals instead.
      return typeof inner.contract_id === "string";
    }

    return (
      typeof inner.search_type === "string" &&
      typeof inner.original_query === "string" &&
      typeof inner.search_results === "object" &&
      inner.search_results !== null
    );
  }

  if (inner.status === "not_supported") {
    return typeof inner.reason === "string";
  }

  if (inner.status === "error") {
    return true;
  }

  // Accept unknown statuses — normalizer will convert to error result. This is
  // also the path "skipped" takes: it asserts on no field the backend could drop,
  // so a branch of its own would only add a silent-drop hazard for no gain.
  if (typeof inner.status === "string") {
    return true;
  }

  // No status at all — still accept as catylex result so normalizer can handle
  return true;
}

// --- Normalizer ---

/**
 * Normalizes a raw Catylex search result into a flat UI-friendly structure.
 * Matches the backend's transform_response() output.
 * Returns null if the input doesn't pass the type guard.
 */
export function normalizeCatylexSearchResult(obj: unknown): NormalizedCatylexSearchResult | null {
  if (!isCatylexSearchResult(obj)) return null;

  const content = obj.content;

  if (content.status === "success" && content.action === "read") {
    const readContent = content as CatylexReadContent;

    return {
      status: "success",
      action: "read",
      // The read action takes a contract_id, not a query; the section header
      // and info bar both skip an empty query rather than printing "unknown".
      originalQuery: "",
      contractId: readContent.contract_id,
      fileName: readContent.file_name,
      displayTitle: readContent.display_title,
      // Required-but-possibly-empty on the wire: "" is the "no URL" signal, not
      // null. Only a search can supply it — no read-path endpoint returns it, so
      // "" is the normal cross-turn case and `useChatAnalysis` back-fills it from
      // session state. Normalize so the card has a single emptiness test.
      allTermsReportUrl: readContent.all_terms_report_url ?? "",
      workspace: readContent.workspace,
      totalSentences: readContent.total_sentences,
      sentencesReturned: readContent.sentences_returned,
      // Real, not a placeholder: a contract longer than the report's per-request
      // window comes back windowed rather than refused, and the sentence counts
      // say exactly how much is missing.
      truncated: readContent.truncated === true,
      tool_use_id: obj.tool_use_id,
    };
  }

  if (content.status === "success") {
    const successContent = content as CatylexSearchSuccessContent;
    const results = successContent.search_results;

    return {
      status: "success",
      action: "search",
      searchType: successContent.search_type,
      originalQuery: successContent.original_query,
      interpretedQuery: successContent.interpreted_query,
      totalResults: results.total,
      resultsSummary: results.number_of_shown,
      contracts: results.contracts,
      sentences: results.sentences,
      paginationMode: successContent.pagination_mode,
      tool_use_id: obj.tool_use_id,
    };
  }

  if (content.status === "not_supported") {
    const notSupported = content as CatylexSearchNotSupportedContent;
    return {
      status: "not_supported",
      action: "search",
      originalQuery: notSupported.original_query,
      reason: notSupported.reason,
      suggestions: notSupported.suggestions,
      availableConcepts: notSupported.available_concepts,
      tool_use_id: obj.tool_use_id,
    };
  }

  if (content.status === "error") {
    const errorContent = content as CatylexSearchErrorContent;
    // A failed read and a failed search are different stories for the user —
    // "the document didn't load" vs "no matches" — so keep them distinct.
    const action = errorContent.action === "read" ? "read" : "search";
    return {
      status: "error",
      action,
      originalQuery: errorContent.original_query || (action === "read" ? "" : "unknown"),
      // The DETAIL variants, not the summaries: this value renders in the
      // expanded body, where repeating the header teaches the user nothing.
      errorMessage:
        errorContent.message ||
        (action === "read" ? CATYLEX_READ_ERROR_DETAIL : CATYLEX_SEARCH_ERROR_DETAIL),
      errorCode: errorContent.error_code,
      contractId: errorContent.contract_id ?? undefined,
      tool_use_id: obj.tool_use_id,
    };
  }

  if (content.status === "skipped") {
    const skipped = content as CatylexSearchSkippedContent;

    return {
      status: "skipped",
      // Always a search. A read is never skipped, so there is nothing to
      // discriminate here the way the error branch above has to.
      action: "search",
      originalQuery: skipped.original_query || "",
      // No fallback: the header falls back to CATYLEX_SKIPPED_SUMMARY when this
      // is absent, which keeps the "backend states the fact" rule intact rather
      // than inventing a sentence and attributing it to the backend.
      skippedMessage: skipped.message,
      tool_use_id: obj.tool_use_id,
    };
  }

  // Unknown status — return error result instead of null
  const inner = content as Record<string, unknown>;
  return {
    status: "error",
    action: inner.action === "read" ? "read" : "search",
    originalQuery: typeof inner.original_query === "string" ? inner.original_query : "unknown",
    errorMessage: `Unexpected response status: ${String(inner.status || "unknown")}`,
    tool_use_id: obj.tool_use_id,
  };
}

// --- Input parser ---

/**
 * Parses a Catylex tool input from accumulated input_json_delta data.
 * Drives the action-aware processing indicator before the result arrives.
 */
export function parseCatylexToolInput(json: Record<string, unknown>): CatylexToolInput | null {
  const action = json.action;
  if (action !== "search" && action !== "read") return null;

  return {
    action,
    query: typeof json.query === "string" ? json.query : undefined,
    contractId: typeof json.contract_id === "string" ? json.contract_id : undefined,
  };
}
